import { useState, useMemo } from 'react';
import type { WeekType, Location } from '@functieloonhuis/shared';
import { useAllSchedules, useCurrentWeekType, useHolidays } from '../hooks/useWorkSchedules';
import { hasWeekData, getDefaultWeek } from '../utils/weekUtils';
import { getMondayOfWeek, getWorkweekDates, getWorkdaysOfMonth } from '../utils/dateUtils';
import { mapScheduleToWeek, mapScheduleToMonth } from '../utils/planningMapper';
import { WeekToggle } from './WeekToggle';
import { TeamWeekGrid } from './TeamWeekGrid';
import { OrgScheduleFilters } from './OrgScheduleFilters';
import { ScheduleStats } from './ScheduleStats';
import { ViewToggle, type ViewMode } from './ViewToggle';
import { ViewModeSelector, type PlanningViewMode } from './ViewModeSelector';
import { DateNavigator } from './DateNavigator';
import { PlanningWeekGrid } from './PlanningWeekGrid';
import { PlanningDayView } from './PlanningDayView';
import { PlanningMonthGrid } from './PlanningMonthGrid';
import { PlanningStats } from './PlanningStats';

export function OrgScheduleOverview() {
  const { data: allData, isLoading } = useAllSchedules();
  const { data: weekData } = useCurrentWeekType();

  const employees = allData?.data ?? [];
  const currentWeekType = weekData?.data?.currentWeekType;

  // View toggle
  const [viewMode, setViewMode] = useState<ViewMode>('werkrooster');

  // ── Werkdagen (pattern) view state ──
  const anyHasWeekB = employees.some(emp => hasWeekData(emp.weekB));
  const [selectedWeek, setSelectedWeek] = useState<WeekType | null>(null);
  const effectiveWeek = selectedWeek ?? getDefaultWeek(currentWeekType, anyHasWeekB);

  // ── Werkrooster (planning) view state ──
  const [planningViewMode, setPlanningViewMode] = useState<PlanningViewMode>('week');
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());

  const weekStart = useMemo(() => getMondayOfWeek(currentDate), [currentDate]);
  const weekDates = useMemo(() => getWorkweekDates(weekStart), [weekStart]);
  const monthWorkdays = useMemo(
    () => getWorkdaysOfMonth(currentDate.getFullYear(), currentDate.getMonth()),
    [currentDate],
  );

  // Holidays
  const yearStart = planningViewMode === 'maand' ? currentDate.getFullYear() : weekStart.getFullYear();
  const yearEnd = planningViewMode === 'week' ? (weekDates[weekDates.length - 1]?.getFullYear() ?? yearStart) : yearStart;
  const { data: holidaysStart } = useHolidays(yearStart);
  const { data: holidaysEnd } = useHolidays(yearEnd);

  const allHolidays = useMemo(() => {
    const items = [...(holidaysStart?.data ?? [])];
    if (yearEnd !== yearStart) items.push(...(holidaysEnd?.data ?? []));
    return items;
  }, [holidaysStart, holidaysEnd, yearStart, yearEnd]);

  // ── Filters ──
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  const departments = useMemo(() => {
    const deptMap = new Map<number, string>();
    employees.forEach(emp => {
      if (emp.employee.departmentId && emp.employee.departmentName) {
        deptMap.set(emp.employee.departmentId, emp.employee.departmentName);
      }
    });
    return Array.from(deptMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [employees]);

  // Department filter always applies
  const deptFilteredEmployees = useMemo(() => {
    if (!selectedDepartment) return employees;
    return employees.filter(emp => emp.employee.departmentId === selectedDepartment);
  }, [employees, selectedDepartment]);

  // ── Werkdagen view: additional location filter ──
  const filteredEmployees = useMemo(() => {
    let result = deptFilteredEmployees;
    if (selectedLocation) {
      result = result.filter(emp => {
        const weekEntries = effectiveWeek === 'A' ? emp.weekA : emp.weekB;
        return weekEntries.some(day =>
          day.isWorkday && day.location === selectedLocation &&
          (selectedDay ? day.dayOfWeek === selectedDay : true)
        );
      });
    }
    return result;
  }, [deptFilteredEmployees, selectedLocation, selectedDay, effectiveWeek]);

  // ── Planning view: mapped data ──
  const plannedWeek = useMemo(
    () => mapScheduleToWeek(deptFilteredEmployees, weekStart, allHolidays),
    [deptFilteredEmployees, weekStart, allHolidays],
  );
  const plannedMonth = useMemo(
    () => mapScheduleToMonth(deptFilteredEmployees, currentDate.getFullYear(), currentDate.getMonth(), allHolidays),
    [deptFilteredEmployees, currentDate, allHolidays],
  );

  const filteredPlannedWeek = useMemo(() => {
    if (!selectedLocation) return plannedWeek;
    return plannedWeek.filter(pw =>
      pw.days.some(day =>
        day.isWorkday && day.location === selectedLocation &&
        (selectedDay ? day.dayOfWeek === selectedDay : true)
      )
    );
  }, [plannedWeek, selectedLocation, selectedDay]);

  const filteredPlannedMonth = useMemo(() => {
    if (!selectedLocation) return plannedMonth;
    return plannedMonth.filter(pw =>
      pw.days.some(day =>
        day.isWorkday && day.location === selectedLocation &&
        (selectedDay ? day.dayOfWeek === selectedDay : true)
      )
    );
  }, [plannedMonth, selectedLocation, selectedDay]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-sm text-gray-500">Werkroosters laden...</span>
        </div>
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="mt-4 text-gray-500">Er zijn nog geen werkroosters ingevuld</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      {viewMode === 'werkdagen' ? (
        <ScheduleStats employees={filteredEmployees} selectedWeek={effectiveWeek} />
      ) : (
        <PlanningStats planned={planningViewMode === 'week' ? filteredPlannedWeek : plannedWeek} />
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-gray-900">
              Werkrooster overzicht
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              {viewMode === 'werkdagen' && currentWeekType && anyHasWeekB && (
                <span className="text-xs text-gray-400">
                  Huidige week: {currentWeekType}
                </span>
              )}
              {viewMode === 'werkdagen' && anyHasWeekB && (
                <WeekToggle
                  selectedWeek={effectiveWeek}
                  onWeekChange={setSelectedWeek}
                  currentWeekType={currentWeekType}
                  hasWeekB={anyHasWeekB}
                />
              )}
              <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
            </div>
          </div>

          {viewMode === 'werkrooster' && (
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <ViewModeSelector viewMode={planningViewMode} onViewModeChange={setPlanningViewMode} />
              <DateNavigator mode={planningViewMode} date={currentDate} onDateChange={setCurrentDate} />
            </div>
          )}

          <OrgScheduleFilters
            selectedDepartment={selectedDepartment}
            selectedDay={selectedDay}
            selectedLocation={selectedLocation}
            departments={departments}
            onDepartmentChange={setSelectedDepartment}
            onDayChange={setSelectedDay}
            onLocationChange={setSelectedLocation}
          />
        </div>

        {/* Scrollable grid */}
        <div className="max-h-[600px] overflow-y-auto">
          <div className="p-4">
            {viewMode === 'werkdagen' ? (
              filteredEmployees.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">Geen medewerkers gevonden met de huidige filters</p>
                </div>
              ) : (
                <TeamWeekGrid
                  employees={filteredEmployees}
                  selectedWeek={effectiveWeek}
                  filteredDay={selectedDay}
                  filteredLocation={selectedLocation}
                  showDepartment
                  stickyHeader
                />
              )
            ) : planningViewMode === 'week' ? (
              filteredPlannedWeek.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">Geen medewerkers gevonden met de huidige filters</p>
                </div>
              ) : (
                <PlanningWeekGrid
                  planned={filteredPlannedWeek}
                  weekDates={weekDates}
                  filteredDay={selectedDay}
                  filteredLocation={selectedLocation}
                  showDepartment
                  stickyHeader
                />
              )
            ) : planningViewMode === 'dag' ? (
              <PlanningDayView
                planned={plannedWeek}
                date={currentDate}
                filteredLocation={selectedLocation}
              />
            ) : (
              filteredPlannedMonth.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">Geen medewerkers gevonden met de huidige filters</p>
                </div>
              ) : (
                <PlanningMonthGrid
                  planned={filteredPlannedMonth}
                  workdays={monthWorkdays}
                  filteredDay={selectedDay}
                  filteredLocation={selectedLocation}
                  showDepartment
                  stickyHeader
                />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
